import type { PDPProductThumbnail } from "@components/commerce/product/right-panel/buy-card/types";
import type { Product } from "@graphql/generated/components";
import { Attributes } from "@lib/enums/ProductAttributes";
import { getBackgroundColorValue, getProductAttributeValue } from "@lib/utils/attributesUtils";
import type { AlgoliaProduct } from "@lib/wrappers/algolia";

export const isAnyVariantsInStock = (product: Product): boolean => {
    const results = [];

    product.masterData?.current.variants.forEach((variant) => {
        variant.availability.channels.results.forEach((r) => {
            results.push(r.availability.isOnStock);
        });
    });

    return results.some((r) => r === true);
};

export const isAllVariantsInStock = (product: Product): boolean => {
    const results = [];

    product.masterData?.current.variants.forEach((variant) => {
        variant.availability.channels.results.forEach((r) => {
            results.push(r.availability.isOnStock);
        });
    });

    return results.every((r) => r === true);
};

export const getProductImageThumbnails = (
    product: Product,
    locale: string
): PDPProductThumbnail => {
    const { attributesRaw, images } = product.masterData?.current.masterVariant || {};
    const colorCode = getProductAttributeValue(attributesRaw, Attributes.colorCode);
    const articleNumber = getProductAttributeValue(attributesRaw, Attributes.articleNumber);
    const dominantColor = getProductAttributeValue(attributesRaw, Attributes.dominantColor, locale);
    const pageTitle = getProductAttributeValue(attributesRaw, Attributes.pageTitle, locale);
    const backgroundColor = getBackgroundColorValue(attributesRaw);
    const [firstImage] = images;

    const available = isAnyVariantsInStock(product);

    return {
        productName: product.masterData.current.name,
        articleNumber,
        dominantColor,
        colorCode,
        image: {
            url: firstImage.url,
            label: firstImage.label ?? "",
        },
        available,
        backgroundColor,
        pageTitle,
    };
};

export const getRelatedProductImageThumbnails = (product: AlgoliaProduct): PDPProductThumbnail[] =>
    product?.productColors?.map((relatedProduct) => ({
        image: {
            url: relatedProduct.image ?? "",
            label: relatedProduct.imageLabel ?? "",
        },
        productName: relatedProduct.name ?? product.name,
        articleNumber: product.articleNumber,
        dominantColor: relatedProduct.dominantColor ?? product.dominantColor,
        colorCode: relatedProduct.colorCode,
        available: relatedProduct.hasStock,
        ...(product.pageTitle && { pageTitle: product.pageTitle }),
    })) ?? [];

export const calculatePrice = (centAmount: number, fractionDigits: number) => {
    const divisor = Math.pow(10, fractionDigits);
    return (centAmount / divisor).toFixed(fractionDigits);
};

export const calculatePriceValidUntil = (
    discountPercentage: number | Record<string, never>
): Date => {
    const priceValidUntil: Date = new Date();
    if (typeof discountPercentage === "number") {
        priceValidUntil.setHours(23, 59, 59, 999);
    } else {
        priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
    }
    return priceValidUntil;
};
